import {FC, useEffect, useState} from 'react'
import {useMutation, useQueryClient} from 'react-query'
import {MenuComponent} from '../../../../../../../_metronic/assets/ts/components'
import {KTSVG} from '../../../../../../../_metronic/helpers'
import {useQueryResponse} from '../../core/QueryResponseProvider'
import {deleteDataRecord} from '../../core/_requests'
import toaster from '../../../../../../../Utils/toaster'
import axios from 'axios'

type Props = {
  customerIVRID?: number
  code: string
  value: string
}

const ActionsCell: FC<Props> = ({customerIVRID, code, value}) => {
  const {query, refetch} = useQueryResponse()
  const queryClient = useQueryClient()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Handle modal body class for proper animation
  useEffect(() => {
    if (isDeleteModalOpen) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
    
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [isDeleteModalOpen])

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  const deleteItem = useMutation(() => deleteDataRecord(customerIVRID!), {
    onSuccess: () => {
      queryClient.invalidateQueries([`multiple-ivr-list-${query}`])
      toaster('success', 'IVR number deleted successfully')
      setIsDeleteModalOpen(false)
      setIsDeleting(false) // Reset loading state
      refetch() // Refresh the table data
    },
    onError: () => {
      toaster('error', 'Failed to delete IVR number')
      setIsDeleting(false) // Reset loading state
    },
    onSettled: () => {
      // This runs after both success and error
      setIsDeleting(false) // Ensure loading state is always reset
    }
  })

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (customerIVRID) {
      setIsDeleting(true)
      deleteItem.mutate()
    }
  }

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false)
    setIsDeleting(false) // Reset loading state when canceling
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancelDelete()
    }
  }

  return (
    <>
      <div className='d-flex justify-content-end flex-shrink-0'>
        <button
          className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
          onClick={handleDeleteClick}
        >
          <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <>
          <div
            className='modal fade show d-block'
            id='kt_modal_delete_ivr'
            role='dialog'
            tabIndex={-1}
            aria-modal='true'
            onClick={handleBackdropClick}
          >
            <div className='modal-dialog modal-dialog-centered mw-500px'>
              <div className='modal-content'>
                <div className='modal-header py-3'>
                  <h2 className='fw-semibolder fs-3 text-danger'>Delete IVR Number</h2>
                  <div
                    className='btn btn-icon btn-sm btn-active-icon-primary'
                    onClick={handleCancelDelete}
                    style={{cursor: 'pointer'}}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
                  </div>
                </div>
                <div className='modal-body scroll-y'>
                  <div className='mb-5'>
                    <div className='text-center mb-5'>
                      <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-5x text-danger mb-3' />
                      <h3 className='fw-bold text-gray-900 mb-3'>Are you sure you want to delete this IVR number?</h3>
                      <div className='text-gray-600 fs-6'>
                        <strong>IVR Code:</strong> {code}<br/>
                        <strong>IVR Value:</strong> {value}
                      </div>
                    </div>
                    <div className='text-center'>
                      <p className='text-gray-600 fs-6'>
                        This action cannot be undone. The IVR number will be permanently removed from this customer.
                      </p>
                    </div>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='btn btn-danger'
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                        Deleting...
                      </>
                    ) : (
                      'Yes, Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}
    </>
  )
}

export {ActionsCell}
